import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useDebounce } from '../../utils/debounce';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Card,
    CardContent,
    Alert,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Snackbar,
    tooltipClasses,
    TooltipProps,
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    Search,
    People,
    ExpandMore
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import api from '../../api/axios';
import { useAuth } from '../../contexts/AuthContext';

// --- Interfaces ---
import { Permission, User } from '../../types/user';

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.black,
    },
}));

// Interfaces removed in favor of generic types

interface UserFormData {
    name: string;
    email: string;
    phone: string;
    role: 'admin' | 'sub_admin' | 'user';
    permissions: number[];
}

const Users = () => {
    const queryClient = useQueryClient();
    const [pageModel, setPageModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const [open, setOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    // Form State
    const [formData, setFormData] = useState<UserFormData>({
        name: '',
        email: '',
        phone: '',
        role: 'sub_admin',
        permissions: [],
    });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);

    // Fetch Users
    const { data: usersData, isLoading: usersLoading } = useQuery({
        queryKey: ['users', pageModel.page, pageModel.pageSize, debouncedSearch],
        queryFn: async () => {
            const params: any = { page: pageModel.page + 1, per_page: pageModel.pageSize };
            if (debouncedSearch) params.search = debouncedSearch;
            const res = await api.get('/tenant/users', { params });
            return res.data;
        },
        placeholderData: keepPreviousData,
    });

    // Fetch Permissions
    const { data: permissionsData } = useQuery({
        queryKey: ['permissions'],
        queryFn: async () => {
            const res = await api.get('/tenant/permissions');
            return res.data.data as Permission[];
        },
    });

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleMutationError = (err: any) => {
        if (err.response?.status === 422 && err.response?.data?.errors) {
            const newErrors: Record<string, string> = {};

            Object.entries(err.response.data.errors).forEach(([key, msgs]: [string, any]) => {
                const msg = Array.isArray(msgs) ? msgs[0] : msgs;
                if (['name', 'email', 'phone', 'role'].includes(key)) {
                    newErrors[key] = msg;
                } else {
                    showSnackbar(msg, 'error');
                }
            });
            setFormErrors(newErrors);
        } else {
            showSnackbar(err.response?.data?.message || 'An error occurred', 'error');
        }
    };

    // Mutations
    const saveMutation = useMutation({
        mutationFn: async (data: UserFormData) => {
            if (selectedUser) {
                // Update
                return api.put(`/tenant/users/${selectedUser.id}`, data);
            } else {
                // Create
                return api.post('/tenant/users', data);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            handleClose();
            showSnackbar(`User ${selectedUser ? 'updated' : 'created'} successfully`, 'success');
        },
        onError: handleMutationError
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: number) => {
            return api.delete(`/tenant/users/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            showSnackbar('User deleted successfully', 'success');
        },
        onError: (err: any) => showSnackbar(err.response?.data?.message || 'Failed to delete user', 'error')
    });

    // Handlers
    const handleOpen = (user?: User) => {
        if (user) {
            setSelectedUser(user);
            setFormData({
                name: user.name,
                email: user.email,
                phone: user.phone,
                role: user.role,
                permissions: user.permissions?.map(p => p.id) || []
            });
        } else {
            setSelectedUser(null);
            setFormData({
                name: '',
                email: '',
                phone: '',
                role: 'sub_admin',
                permissions: []
            });
        }
        setFormErrors({});
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedUser(null);
        setFormErrors({});
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        saveMutation.mutate(formData);
    };

    const handleDeleteClick = (user: User) => {
        setUserToDelete(user);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (userToDelete) {
            deleteMutation.mutate(userToDelete.id);
        }
        setDeleteDialogOpen(false);
        setUserToDelete(null);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setUserToDelete(null);
    };

    const handlePermissionToggle = (permId: number) => {
        setFormData(prev => {
            const newPerms = prev.permissions.includes(permId)
                ? prev.permissions.filter(id => id !== permId)
                : [...prev.permissions, permId];
            return { ...prev, permissions: newPerms };
        });
    };

    const { hasPermission } = useAuth();

    // Group Permissions
    const groupedPermissions = permissionsData?.reduce((acc, perm) => {
        const groupName = perm.group?.name || 'Other';
        if (!acc[groupName]) acc[groupName] = [];
        acc[groupName].push(perm);
        return acc;
    }, {} as Record<string, Permission[]>) || {};

    const columns: GridColDef[] = [
        {
            field: 'index',
            headerName: 'Sr.',
            width: 60,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (pageModel.page * pageModel.pageSize) + params.api.getAllRowIds().indexOf(params.row.id) + 1,
        },
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
        { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
        { field: 'phone', headerName: 'Phone', width: 150 },
        {
            field: 'role',
            headerName: 'Role',
            width: 120,
            renderCell: (p) => (
                <Chip
                    label={p.value === 'admin' ? 'Admin' : (p.value === 'sub_admin' ? 'Sub Admin' : 'User')}
                    color={p.value === 'admin' ? 'primary' : 'default'}
                    size="small"
                    variant={p.value === 'admin' ? 'filled' : 'outlined'}
                />
            )
        },
        {
            field: 'created_at',
            headerName: 'Created',
            width: 150,
            valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : 'N/A',
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 130,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    {hasPermission('users.update') && (
                        <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'green' }}>
                            <BootstrapTooltip title="Edit" placement="top">
                                <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => handleOpen(params.row)}>
                                    <Edit fontSize="small" />
                                </IconButton>
                            </BootstrapTooltip>
                        </Button>
                    )}
                    {hasPermission('users.delete') && (
                        <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'red' }}>
                            <BootstrapTooltip title="Delete" placement="top">
                                <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => handleDeleteClick(params.row)}>
                                    <Delete fontSize="small" />
                                </IconButton>
                            </BootstrapTooltip>
                        </Button>
                    )}
                </Box>
            ),
        },
    ];

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <People color="primary" />
                    <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '1.6rem' } }}>User Management</Typography>
                </Box>
                {hasPermission('users.create') && (
                    <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
                        Add User
                    </Button>
                )}
            </Box>

            <Card sx={{ mb: 2 }}>
                <CardContent sx={{ py: 2 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search users..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
                        }}
                    />
                </CardContent>
            </Card>

            <Card>
                <DataGrid
                    rows={usersData?.data?.data || []}
                    columns={columns}
                    loading={usersLoading}
                    paginationModel={pageModel}
                    onPaginationModelChange={setPageModel}
                    pageSizeOptions={[10, 25, 50]}
                    paginationMode="server"
                    rowCount={usersData?.data?.total || 0}
                    autoHeight
                    disableRowSelectionOnClick
                    sx={{ border: 0 }}
                />
            </Card>

            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={window.innerWidth < 600}>
                <DialogTitle fontSize={22}>{selectedUser ? 'Edit User' : 'Add User'}</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent dividers>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label="Name"
                                    fullWidth
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    error={!!formErrors.name}
                                    helperText={formErrors.name}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label="Phone"
                                    fullWidth
                                    required
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    error={!!formErrors.phone}
                                    helperText={formErrors.phone}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label="Email"
                                    type="email"
                                    fullWidth
                                    required
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    error={!!formErrors.email}
                                    helperText={formErrors.email}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth required>
                                    <InputLabel>Role</InputLabel>
                                    <Select
                                        value={formData.role}
                                        label="Role"
                                        onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'sub_admin' | 'user' })}
                                        error={!!formErrors.role}
                                    >
                                        <MenuItem value="sub_admin">Sub Admin</MenuItem>
                                    </Select>
                                    {formErrors.role && <Typography variant="caption" color="error">{formErrors.role}</Typography>}
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 3 }}>
                            <Typography variant="h6" gutterBottom>Permissions</Typography>
                            {formData.role === 'admin' && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    Admins typically have full access, but you can still limit specific permissions if required by the system.
                                </Alert>
                            )}

                            {Object.entries(groupedPermissions).map(([group, perms]) => (
                                <Accordion key={group} defaultExpanded>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{group}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <FormGroup row>
                                            {perms.map((perm) => (
                                                <FormControlLabel
                                                    key={perm.id}
                                                    control={
                                                        <Checkbox
                                                            checked={formData.permissions.includes(perm.id)}
                                                            onChange={() => handlePermissionToggle(perm.id)}
                                                            size="small"
                                                        />
                                                    }
                                                    label={
                                                        <Box>
                                                            <Typography variant="body2">{perm.name}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{perm.description}</Typography>
                                                        </Box>
                                                    }
                                                    sx={{ width: { xs: '100%', sm: '48%' }, mb: 1 }}
                                                />
                                            ))}
                                        </FormGroup>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button variant="outlined" onClick={handleClose}>Cancel</Button>
                        <Button type="submit" color="success" variant="contained" disabled={saveMutation.isPending}>
                            {saveMutation.isPending ? 'Saving...' : 'Save User'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
                <Box sx={{ bgcolor: '#fee2e2', p: 3, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #fecaca' }}>
                    <Box sx={{ bgcolor: '#dc2626', borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Delete sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b' }}>Confirm Delete</Typography>
                        <Typography variant="body2" sx={{ color: '#b91c1c' }}>This action cannot be undone</Typography>
                    </Box>
                </Box>
                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>Are you sure you want to delete <strong>"{userToDelete?.name}"</strong>?</Typography>
                    <Alert severity="warning" sx={{ bgcolor: '#fef3c7', border: '1px solid #fcd34d', '& .MuiAlert-icon': { color: '#d97706' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Warning: You will not be able to revert this action!</Typography>
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1, bgcolor: '#f9fafb' }}>
                    <Button variant="outlined" onClick={handleDeleteCancel} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteMutation.isPending}
                        startIcon={<Delete />} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Users;
